
/**
 * Module dependencies.
 */

import { memoize } from 'lodash';
import Bowser from 'bowser';
import React, { ReactNode, createContext, useContext } from 'react';

/**
 * `PlatformType` type.
 */

type PlatformType = 'desktop' | 'mobile' | 'tablet';

/**
 * Export `Browser` type.
 */

export type Browser = {
  getBrowserName: () => string;
  getOSName: () => string;
  getPlatformType: () => PlatformType;
  getUA: () => string;
  is: (value: string) => boolean;
  some: (items: Array<string>) => boolean;
};

/**
 * Browser context.
 */

const BrowserContext = createContext<Browser | null>(null);

/**
 * Export `useBrowser`.
 */

export function useBrowser(): Browser {
  const browser = useContext(BrowserContext);

  if (!browser) {
    throw new Error('Missing browser context');
  }

  return browser;
}

/**
 * `BrowserProviderProps` type.
 */

type BrowserProviderProps = {
  children: ReactNode;
  userAgent: string;
};

/**
 * Parse browser.
 */

const parseBrowser = memoize((userAgent: string): Browser => {
  const parser = Bowser.getParser(userAgent);

  return {
    getBrowserName: () => parser.getBrowserName(),
    getOSName: () => parser.getOSName(),
    getPlatformType: () => parser.getPlatformType() as PlatformType,
    getUA: () => parser.getUA(),
    is: (value: string) => parser.is(value),
    some: (items: Array<string>) => parser.some(items) ?? false
  };
});

/**
 * Export `BrowserProvider` component.
 */

export function BrowserProvider(props: BrowserProviderProps): JSX.Element {
  const { children, userAgent } = props;

  return (
    <BrowserContext.Provider value={parseBrowser(userAgent)}>
      {children}
    </BrowserContext.Provider>
  );
}
